import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { StorageKey } from '../storage-keys.enum';
import { OnboardingStateService } from '@views/onboarding/services/onboarding-state.service';

// Tab-scoped so a reload preserves the block (BE will keep 429ing on the next
// gated call, but sessionStorage avoids the brief flash of usable wizard chrome
// between reload and the first request).
const RATE_LIMIT_BLOCKED_KEY = 'dr_plg_rate_limit_blocked';
const RATE_LIMIT_MESSAGE_KEY = 'dr_plg_rate_limit_message';

/**
 * Holds browser-storage-backed session state. Two distinct stores:
 *  - `localStorage` for the session id and onboarding-completed flag — must
 *    survive tab close so an in-progress trial isn't lost when the user
 *    reopens the link.
 *  - `sessionStorage` for the rate-limit latch (see `notifyRateLimited`) —
 *    intentionally tab-scoped so a fresh tab on a new BE-side budget window
 *    doesn't start with a stale block.
 *
 * Intentionally contains NO dependency on FetcherService / HttpClient so that
 * HTTP interceptors can inject this service without creating the circular chain:
 *   HTTP_INTERCEPTORS → UserSessionService → FetcherService → HttpClient → HTTP_INTERCEPTORS
 */
@Injectable({ providedIn: 'root' })
export class UserSessionStateService {
    private readonly onboardingState = inject(OnboardingStateService);

    private readonly _sessionInvalidated$ = new Subject<void>();
    readonly sessionInvalidated$ = this._sessionInvalidated$.asObservable();

    private readonly _onboardingProgressCleared$ = new Subject<void>();
    /**
     * Fires when client-side wizard progress is wiped while the session id
     * stays valid — i.e. specifically on a recovery re-bootstrap from a 401
     * (see `SessionRetryInterceptor`). The setup component listens to this
     * to remount step 0 if the user was mid-wizard when their session
     * expired; without it, the same-URL `router.navigate` is a no-op and
     * the user is left visually parked on a step whose data has just been
     * reset under it. Distinct from `sessionInvalidated$`, which also fires
     * during the normal first-time bootstrap (when wizard progress must be
     * preserved).
     */
    readonly onboardingProgressCleared$ = this._onboardingProgressCleared$.asObservable();

    private readonly _sessionExpired$ = new BehaviorSubject<boolean>(false);
    /**
     * Latched signal raised when the BE returns `401 + extensions.code ===
     * "SESSION_EXPIRED"` (sliding 30-minute idle TTL elapsed). Distinct from
     * `signUpRequired$` — the user is still a valid HubSpot contact, only
     * the server-side wizard state is gone. The shell swaps the routed
     * content for `SessionExpiredLandingComponent` while this is `true`, and
     * the CTA on that landing calls `restartAfterSessionExpiry()` to wipe
     * client state and bounce the user to the start of setup (the Welcome
     * step, index 0).
     *
     * Cleared by `resetState()` so the latch can't survive an unrelated
     * full reset (e.g. `PlgTokenService.clearAll()` on bootstrap failure,
     * or `resetForNewContact()` when a different `plg_token` arrives in
     * the URL — that flow would otherwise show the expiry landing to the
     * new contact). The template gives `sessionExpired$` precedence over
     * `signUpRequired$`, so a stuck latch would mask the sign-up landing.
     */
    readonly sessionExpired$: Observable<boolean> = this._sessionExpired$.asObservable();

    private readonly _rateLimitBlocked$ = new BehaviorSubject<boolean>(
        sessionStorage.getItem(RATE_LIMIT_BLOCKED_KEY) === 'true',
    );
    /**
     * Latched signal raised when the BE returns `429 + extensions.code ===
     * "RATE_LIMIT_EXCEEDED"`. Distinct from `sessionExpired$`: the session is
     * still valid, but the anonymous user has exhausted their AI-cost budget
     * (mapping / CoA classification both call out to dr-ai-service). The
     * wizard surfaces a non-dismissable in-card overlay that disables all
     * step interactions and offers an "Unlock full version" CTA pointing at
     * the shared `UpgradeDialogComponent`.
     *
     * Tab-scoped (sessionStorage) so a reload preserves the block — closing
     * the tab clears it, matching the BE's session-bound enforcement window.
     * Cleared by `resetState()` so a contact swap or bootstrap recovery
     * doesn't leave the new session staring at an orphan block.
     */
    readonly rateLimitBlocked$: Observable<boolean> = this._rateLimitBlocked$.asObservable();

    private readonly _rateLimitMessage$ = new BehaviorSubject<string | null>(
        sessionStorage.getItem(RATE_LIMIT_MESSAGE_KEY),
    );
    /** BE-supplied message shown inside the rate-limit overlay; null falls back to a generic copy. */
    readonly rateLimitMessage$: Observable<string | null> = this._rateLimitMessage$.asObservable();

    private readonly _onboardingStepResyncRequested$ = new Subject<void>();
    /**
     * Fires when a step detects its local state is out of sync with the
     * server session and asks the parent to re-fetch + re-restore. Used by
     * Field Mapping when GET /mapping fails because files were deleted out
     * from under it (e.g. user clicked Back, deleted the file, then refresh
     * or navigated forward again). Avoids an unrecoverable error+reload loop.
     */
    readonly onboardingStepResyncRequested$ = this._onboardingStepResyncRequested$.asObservable();

    requestOnboardingStepResync(): void {
        this._onboardingStepResyncRequested$.next();
    }

    hasSession(): boolean {
        return !!localStorage.getItem(StorageKey.SessionId);
    }

    getSessionId(): string | null {
        return localStorage.getItem(StorageKey.SessionId);
    }

    saveSessionId(sessionId: string): void {
        localStorage.setItem(StorageKey.SessionId, sessionId);
    }

    clearSession(): void {
        localStorage.removeItem(StorageKey.SessionId);
    }

    /**
     * Drops the stored session id and emits `sessionInvalidated$` so cached
     * session-derived state (e.g. `UserSessionService.session$`) is marked
     * stale. Lighter than `resetState()` — does NOT touch onboarding state.
     *
     * Used inside `PlgTokenService.performBootstrap` to clear the old id
     * before POST /sessions returns the new one. Onboarding-state cleanup
     * after a recovery re-bootstrap is the `SessionRetryInterceptor`'s job
     * (via `clearOnboardingProgress`), because only the interceptor knows
     * whether this bootstrap was a recovery from a 401 (server-side session
     * gone, client wizard progress now stale) versus the wizard's first
     * intentional `createSession()` call (state must be preserved).
     */
    invalidateSession(): void {
        this.clearSession();
        this._sessionInvalidated$.next();
    }

    isOnboardingCompleted(): boolean {
        return localStorage.getItem(StorageKey.OnboardingCompleted) === 'true';
    }

    completeOnboarding(): void {
        localStorage.setItem(StorageKey.OnboardingCompleted, 'true');
    }

    clearOnboardingCompleted(): void {
        localStorage.removeItem(StorageKey.OnboardingCompleted);
    }

    /**
     * Drops every piece of client-side onboarding progress while leaving the
     * (freshly minted) session id in place. Called from
     * `SessionRetryInterceptor` after a recovery re-bootstrap: the server
     * just gave us an empty session, so the in-memory wizard snapshot and
     * the `OnboardingCompleted` flag both refer to data the server no longer
     * has. Does NOT emit `sessionInvalidated$` — the session id is valid,
     * only the progress around it is stale.
     */
    clearOnboardingProgress(): void {
        this.onboardingState.reset();
        this.clearOnboardingCompleted();
        this._onboardingProgressCleared$.next();
    }

    resetState(): void {
        this.onboardingState.reset();

        this.clearSession();
        this.clearOnboardingCompleted();

        // Drop the session-expired latch too — any caller asking for a full
        // reset (the CTA, bootstrap failure via PlgTokenService.clearAll(),
        // a contact swap via resetForNewContact()) wants the next render to
        // come from the routed wizard, not a stale expiry landing.
        this._sessionExpired$.next(false);

        // Same logic for the rate-limit latch: a fresh session starts with a
        // fresh BE-side budget, so the FE block must not bleed across resets.
        this._clearRateLimitLatch();

        this._sessionInvalidated$.next();
    }

    /**
     * Latches the session-expired landing on. Called from the 401 interceptor
     * when the BE response carries `extensions.code === "SESSION_EXPIRED"`.
     * Idempotent: re-emitting `true` while already latched is a no-op for
     * subscribers behind `distinctUntilChanged` and harmless otherwise.
     */
    notifySessionExpired(): void {
        if (!this._sessionExpired$.value) {
            this._sessionExpired$.next(true);
        }
    }

    /**
     * CTA handler for `SessionExpiredLandingComponent`. Drops the now-stale
     * session id and onboarding progress, releases the landing latch, and
     * leaves session minting to the wizard's normal flow — the next
     * session-requiring action (`UserSessionService.createSession()` on the
     * Welcome → ERP-Next click, or any gated call) will bootstrap a fresh
     * id against the same `plg_token`. Caller is responsible for the
     * `router.navigate(['/onboarding/setup'])` because this service does not
     * depend on `Router`.
     */
    restartAfterSessionExpiry(): void {
        this.resetState();
    }

    /**
     * Latches the rate-limit overlay on. Called from `RateLimitInterceptor`
     * when the BE returns `429 + extensions.code === "RATE_LIMIT_EXCEEDED"`
     * (anonymous user exhausted their AI-cost budget). Idempotent: re-latching
     * just refreshes the displayed message in case the BE varies copy across
     * subsequent 429s. The block is intentionally terminal for the tab — the
     * only way out is the "Unlock full version" CTA.
     */
    notifyRateLimited(message: string | null): void {
        // Normalize once so sessionStorage and the stream agree on what "no
        // message" means. Without this, an empty-string from the BE would be
        // dropped from storage but still emitted as "", and a subscriber that
        // doesn't itself normalize would render differently before vs. after
        // a reload.
        const normalized = message?.trim() ? message : null;

        sessionStorage.setItem(RATE_LIMIT_BLOCKED_KEY, 'true');
        if (normalized) {
            sessionStorage.setItem(RATE_LIMIT_MESSAGE_KEY, normalized);
        } else {
            sessionStorage.removeItem(RATE_LIMIT_MESSAGE_KEY);
        }
        this._rateLimitMessage$.next(normalized);
        if (!this._rateLimitBlocked$.value) {
            this._rateLimitBlocked$.next(true);
        }
    }

    /**
     * Synchronous read so non-reactive callers (component error handlers that
     * decide whether to fire a toast) can short-circuit without subscribing.
     */
    isRateLimited(): boolean {
        return this._rateLimitBlocked$.value;
    }

    private _clearRateLimitLatch(): void {
        sessionStorage.removeItem(RATE_LIMIT_BLOCKED_KEY);
        sessionStorage.removeItem(RATE_LIMIT_MESSAGE_KEY);
        if (this._rateLimitBlocked$.value) {
            this._rateLimitBlocked$.next(false);
        }
        this._rateLimitMessage$.next(null);
    }
}
